/**
 * Application Routes
 * Centralized route configuration using React Router v6
 */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../hooks/useAuth';

// Layouts
import DashboardLayout from '../../layouts/DashboardLayout/DashboardLayout';
import PublicLayout from '../../layouts/PublicLayout/PublicLayout';

// Dashboard
import DashboardPage from '../../features/dashboard/pages/DashboardPage';

// Members Pages
import MembersListPage from '../../features/members/pages/MembersList';
import RegisterMemberPage from '../../features/members/pages/RegisterMember';
import UpdateMemberPage from '../../features/members/pages/UpdateMember';
import MemberProfilePage from '../../features/members/pages/MemberProfile';

// Loans Pages
import LoansListPage from '../../features/loans/pages/LoansList';
import CreateLoanPage from '../../features/loans/pages/CreateLoan';
import LoanDetailsPage from '../../features/loans/pages/LoanDetails';
import LoanApplicationsPage from '../../features/loans/pages/LoanApplications';

// Savings Pages
import SavingsListPage from '../../features/savings/pages/SavingsList';
import CreateSavingsPage from '../../features/savings/pages/CreateSavings';

// Shares Pages
import SharesListPage from '../../features/shares/pages/SharesList';
import PurchaseSharesPage from '../../features/shares/pages/PurchaseShares';

// Repayments Pages
import RepaymentsListPage from '../../features/repayments/pages/RepaymentsList';
import CreateRepaymentPage from '../../features/repayments/pages/CreateRepayment';

// Guarantors Pages
import GuarantorsListPage from '../../features/guarantors/pages/GuarantorsList';
import CreateGuarantorPage from '../../features/guarantors/pages/CreateGuarantor';

// Accounting Pages
import AccountingDashboard from '../../features/accounting/pages/AccountingDashboard';
import TransactionsPage from '../../features/accounting/pages/TransactionsPage';
import ChartOfAccountsPage from '../../features/accounting/pages/ChartOfAccountsPage';

// Reports Pages
import ReportsDashboard from '../../features/reports/pages/ReportsDashboard';
import MembersReport from '../../features/reports/pages/MembersReport';
import LoansReport from '../../features/reports/pages/LoansReport';

// Users Pages
import UsersListPage from '../../features/users/pages/UsersList';
import CreateUserPage from '../../features/users/pages/CreateUser';
import RolesPage from '../../features/users/pages/RolesPage';

// Settings Pages
import GeneralSettings from '../../features/settings/pages/GeneralSettings';
import OrganizationSettings from '../../features/settings/pages/OrganizationSettings';
import FinancialSettings from '../../features/settings/pages/FinancialSettings';

// Common Pages
import NotFoundPage from '../../components/common/EmptyState/NotFoundPage';

// Landing Page
import LandingPage from '../../features/landing/pages/LandingPage';
import AboutPage from '../../features/landing/pages/AboutPage';
import WhoAreWePage from '../../features/landing/pages/WhoAreWePage';
import OrganizationStructurePage from '../../features/landing/pages/OrganizationStructurePage';
import ContactPage from '../../features/landing/pages/ContactPage';
import LoanProductsPage from '../../features/landing/pages/LoanProductsPage';
import SavingsSharesPage from '../../features/landing/pages/SavingsSharesPage';
import NewsAndEventsPage from '../../features/landing/pages/NewsAndEventsPage';
import NewsDetailPage from '../../features/landing/pages/NewsDetailPage';
import PrivacyPolicyPage from '../../features/landing/pages/PrivacyPolicyPage';
import DownloadsPage from '../../features/landing/pages/DownloadsPage';

// Service Pages
import BecomeMemberPage from '../../features/services/pages/BecomeMemberPage';
import BillPaymentsPage from '../../features/services/pages/BillPaymentsPage';

/**
 * Protected Route Component
 * Redirects to home if not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return children;
};

/**
 * Application Routes Component
 * @returns {JSX.Element} Routes configuration
 */
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Website Routes */}
      <Route element={<PublicLayout />}>
        <Route path={ROUTES.HOME} element={<LandingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/about/who-are-we" element={<WhoAreWePage />} />
        <Route path="/about/organization-structure" element={<OrganizationStructurePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/savings-shares" element={<SavingsSharesPage />} />
        <Route path="/loan-products" element={<LoanProductsPage />} />
        <Route path="/services/become-a-member" element={<BecomeMemberPage />} />
        <Route path="/services/bill-payments" element={<BillPaymentsPage />} />
        <Route path="/news-events" element={<NewsAndEventsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/news-events/:id" element={<NewsDetailPage />} />
        <Route path="/downloads" element={<DownloadsPage />} />
        {/* Additional public pages can be added here */}
      </Route>

      {/* Protected Dashboard Routes */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />

        {/* Members Routes */}
        <Route path={ROUTES.MEMBERS.LIST} element={<MembersListPage />} />
        <Route path={ROUTES.MEMBERS.CREATE} element={<RegisterMemberPage />} />
        <Route path={ROUTES.MEMBERS.EDIT} element={<UpdateMemberPage />} />
        <Route path={ROUTES.MEMBERS.PROFILE} element={<MemberProfilePage />} />

        {/* Loans Routes */}
        <Route path={ROUTES.LOANS.LIST} element={<LoansListPage />} />
        <Route path={ROUTES.LOANS.CREATE} element={<CreateLoanPage />} />
        <Route path={ROUTES.LOANS.DETAILS} element={<LoanDetailsPage />} />
        <Route
          path={ROUTES.LOANS.APPLICATIONS}
          element={<LoanApplicationsPage />}
        />

        {/* Savings Routes */}
        <Route path={ROUTES.SAVINGS.LIST} element={<SavingsListPage />} />
        <Route path={ROUTES.SAVINGS.CREATE} element={<CreateSavingsPage />} />

        {/* Shares Routes */}
        <Route path={ROUTES.SHARES.LIST} element={<SharesListPage />} />
        <Route path={ROUTES.SHARES.PURCHASE} element={<PurchaseSharesPage />} />

        {/* Repayments Routes */}
        <Route path={ROUTES.REPAYMENTS.LIST} element={<RepaymentsListPage />} />
        <Route
          path={ROUTES.REPAYMENTS.CREATE}
          element={<CreateRepaymentPage />}
        />

        {/* Guarantors Routes */}
        <Route path={ROUTES.GUARANTORS.LIST} element={<GuarantorsListPage />} />
        <Route
          path={ROUTES.GUARANTORS.CREATE}
          element={<CreateGuarantorPage />}
        />

        {/* Accounting Routes */}
        <Route
          path={ROUTES.ACCOUNTING.DASHBOARD}
          element={<AccountingDashboard />}
        />
        <Route
          path={ROUTES.ACCOUNTING.TRANSACTIONS}
          element={<TransactionsPage />}
        />
        <Route
          path={ROUTES.ACCOUNTING.CHART_OF_ACCOUNTS}
          element={<ChartOfAccountsPage />}
        />

        {/* Reports Routes */}
        <Route path={ROUTES.REPORTS.DASHBOARD} element={<ReportsDashboard />} />
        <Route path={ROUTES.REPORTS.MEMBERS} element={<MembersReport />} />
        <Route path={ROUTES.REPORTS.LOANS} element={<LoansReport />} />

        {/* Users Routes */}
        <Route path={ROUTES.USERS.LIST} element={<UsersListPage />} />
        <Route path={ROUTES.USERS.CREATE} element={<CreateUserPage />} />
        <Route path={ROUTES.USERS.ROLES} element={<RolesPage />} />

        {/* Settings Routes */}
        <Route
          path={ROUTES.SETTINGS.GENERAL}
          element={<GeneralSettings />}
        />
        <Route
          path={ROUTES.SETTINGS.ORGANIZATION}
          element={<OrganizationSettings />}
        />
        <Route
          path={ROUTES.SETTINGS.FINANCIAL}
          element={<FinancialSettings />}
        />

        {/* Default redirect for unknown dashboard routes */}
        <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
      </Route>

      {/* 404 Not Found */}
      <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
